<script>
export default {
	name: 'BaseVideo',
	inheritAttrs: false,
};
</script>

<script setup>
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import 'videojs-youtube/dist/Youtube.js';
import { pushAA_click } from '~/logic/adobe-analytic.js';

const props = defineProps({
	type: { type: String, default: '' },
	option: { type: Object, default: null },
	src: { type: String, default: '' },
	poster: { type: String, default: '' },
	thumbnailAltText: { type: String, default: '' },
	videoAltText: { type: String, default: '' },
	componentIdAA: { type: String, default: '' },
});

const emit = defineEmits([
	'video-click:callback',
]);

const rootEl = ref(null);
const player = ref(null);

const videoConfig = computed(() => {
	let config = null;
	if (props.type === 'youtube') {
		config = {
			techOrder: ['youtube'],
			sources: [{
				type: 'video/youtube',
				src: `https://www.youtube.com/watch?v=${props.src}`,
			}],
			youtube: {
				'iv_load_policy': 1,
			},
		};

		return config;
	}

	config = {
		autoplay: false,
		controls: true,
		sources: [
			{
				src: `${props.src}`,
				type: 'video/mp4',
			},
		],
		...props.option,
	};

	return config;
	
});

const siteName = window.siteName;

const dataLayer = ({ type, videoSrc, componentID, webName }) => {
	const _clickName = type === 'youtube' ? `https://www.youtube.com/watch?v=${videoSrc}` : videoSrc;
	const data = [
		{
			clickName: _clickName,
			clickComponentType: 'URL',
			componentName: 'BaseVideo',
			componentID,
		},
		{
			name: siteName === 'MHH' ? `click:video:${webName}` : webName,
			type: 'other',
		},
	];

	if (siteName === 'MHH') data.push({ event: 'clickVideo' });

	return data;
};

const aaEventForYoutubeVideo = (e) => {
	const _dataLayer = dataLayer({
		type: 'youtube',
		videoSrc: props.src,
		componentID: props.componentIdAA,
		webName: props.videoAltText,
	});
	
	pushAA_click(_dataLayer);
};

onMounted(() => {
	player.value = videojs(rootEl.value.querySelector('video'), videoConfig.value, () => {
		// player.value.log('onPlayerReady', player.value);
	});
	
	// set up AA event
	if (props.type === 'youtube') {
		setTimeout(() => {
			document.querySelector(`[data-youtube-id="${props.src}"]`).addEventListener('touchstart', aaEventForYoutubeVideo, { once: true });
			document.querySelector(`[data-youtube-id="${props.src}"]`).addEventListener('click', aaEventForYoutubeVideo, { once: true });
		}, 100);
	} else {
		player.value.one('click', function () {
			// AA for html5 video
			const _dataLayer = dataLayer({
				type: 'html5',
				videoSrc: props.src,
				componentID: props.componentIdAA,
				webName: props.src,
			});

			pushAA_click(_dataLayer);
		});
	}
});

onBeforeUnmount(() => {
	player.value?.dispose();
});
</script>

<template>
<div
	ref="rootEl"
	class="BaseVideo"
	:title="`${props.videoAltText ? props.videoAltText : ''}`"
	@click="emit('video-click:callback', $event)"
	@touchend="emit('video-click:callback', $event)"
>
	<video
		v-if="props.type === 'aem'"
		ref="player"
		class="video-js vjs-big-play-centered"
		:poster="props.poster"
		fluid="true"
		:title="props.thumbnailAltText ? props.thumbnailAltText : ''"
	></video>

	<video
		v-else-if="props.type === 'youtube'"
		class="video-js vjs-big-play-centered"
		fluid="true"
		controls
		:data-youtube-id="props.src"
	></video>

	<span v-if="props.type === 'aem' && props.thumbnailAltText" class="sr-only">{{ $t('Thumbnail of video') }}: {{ props.thumbnailAltText }}</span>
	<span v-if="props.videoAltText" class="sr-only">{{ $t('Decription of video') }}: {{ props.videoAltText }}</span>

	<slot name="close-icon"></slot>
</div>
</template>

<style lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.BaseVideo {
	
	.vjs-big-play-button {
		@apply bg-primary-black-base/60;
		border: 0;
		border-radius: 50%;
		width: 48px;
		height: 48px;

		.vjs-icon-placeholder {
			display: block;
			width: 13px;
			height: 18px;
			position: static;
			color: var(--neutral-white-base);
			background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" fill="%23FFFFFF" viewBox="0 0 384 512"><path d="M361 215C375.3 223.8 384 239.3 384 256C384 272.7 375.3 288.2 361 296.1L73.03 472.1C58.21 482 39.66 482.4 24.52 473.9C9.377 465.4 0 449.4 0 432V80C0 62.64 9.377 46.63 24.52 38.13C39.66 29.64 58.21 29.99 73.03 39.04L361 215z"/></svg>');
			@apply mx-auto;
			&::before {
				content: none;
			}
		}
	}
}
</style>
